import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { MOODS_ENDPOINT, MOOD_CATEGORIES_ENDPOINT } from '../config/api';
import { getChartCache, setChartCache, CHART_KEYS } from '../utils/cache';
import AddMoodSheet from './AddMoodSheet';
import './MoodChart.css';

const MOOD_CONFIG = {
  neutral: { label: 'Neutral', emoji: '⚖️', category: 'neutral' },
  joy: { label: 'Joy', emoji: '😊', category: 'positive' },
  love: { label: 'Love', emoji: '❤️', category: 'positive' },
  grateful: { label: 'Grateful', emoji: '🙏', category: 'positive' },
  motivated: { label: 'Motivated', emoji: '💪', category: 'positive' },
  excited: { label: 'Excited', emoji: '🎉', category: 'positive' },
  peaceful: { label: 'Peaceful', emoji: '☮️', category: 'positive' },
  proud: { label: 'Proud', emoji: '🥳', category: 'positive' },
  affectionate: { label: 'Affectionate', emoji: '🥰', category: 'positive' },
  stimulated: { label: 'Stimulated', emoji: '💡', category: 'positive' },
  reflective: { label: 'Reflective', emoji: '🤔', category: 'positive' },
  confident: { label: 'Confident', emoji: '😎', category: 'positive' },
  optimistic: { label: 'Optimistic', emoji: '🌟', category: 'positive' },
  content: { label: 'Content', emoji: '😌', category: 'positive' },
  sad: { label: 'Sad', emoji: '😔', category: 'negative' },
  anxious: { label: 'Anxious', emoji: '😰', category: 'negative' },
  angry: { label: 'Angry', emoji: '😠', category: 'negative' },
  afraid: { label: 'Afraid', emoji: '😨', category: 'negative' },
  guilty: { label: 'Guilty', emoji: '😣', category: 'negative' },
  ashamed: { label: 'Ashamed', emoji: '😳', category: 'negative' },
  lonely: { label: 'Lonely', emoji: '🥺', category: 'negative' },
  frustrated: { label: 'Frustrated', emoji: '😤', category: 'negative' },
  bored: { label: 'Bored', emoji: '😑', category: 'negative' },
  hopelessness: { label: 'Hopelessness', emoji: '😞', category: 'negative' },
  ruminating: { label: 'Ruminating', emoji: '😵‍💫', category: 'negative' },
  numb: { label: 'Numb', emoji: '😶', category: 'negative' },
  unwell: { label: 'Unwell', emoji: '🤕', category: 'negative' },
  'sleep deprived': { label: 'Sleep Deprived', emoji: '😴', category: 'negative' },
  headache: { label: 'Headache', emoji: '🤕', category: 'negative' },
  'work stress': { label: 'Work Stress', emoji: '💼', category: 'negative' },
  hangry: { label: 'Hangry', emoji: '🍔', category: 'negative' },
};

const RANGE_OPTIONS = [
  { id: '2W', label: '2W', days: 14, bucket: 'day', dotR: 5, whiskers: true },
  { id: '1M', label: '1M', days: 30, bucket: 'day', dotR: 4, whiskers: true },
  { id: '3M', label: '3M', days: 90, bucket: 'week', dotR: 4.5, whiskers: true },
  { id: '1Y', label: '1Y', days: 365, bucket: 'week', dotR: 2.5, whiskers: false },
];

const CHART_H = 260;
const PAD_TOP = 20;
const PAD_BOTTOM = 16;
const PAD_X = 12;
const USABLE_H = CHART_H - PAD_TOP - PAD_BOTTOM;
const MS_DAY = 86400000;

/** Soft blue valence ramp: cool slate (−2) → mist (0) → blue (+2) */
function valenceColor(v) {
  const t = (Math.max(-2, Math.min(2, v)) + 2) / 4;
  const stops = [
    [90, 143, 176],
    [120, 168, 196],
    [168, 196, 214],
    [90, 150, 186],
    [61, 126, 166],
  ];
  const scaled = t * (stops.length - 1);
  const i = Math.min(stops.length - 2, Math.floor(scaled));
  const f = scaled - i;
  const a = stops[i];
  const b = stops[i + 1];
  const r = Math.round(a[0] + (b[0] - a[0]) * f);
  const g = Math.round(a[1] + (b[1] - a[1]) * f);
  const bl = Math.round(a[2] + (b[2] - a[2]) * f);
  return `rgb(${r},${g},${bl})`;
}

function parseLocalDate(dateStr) {
  if (!dateStr) return new Date(NaN);
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function formatDateKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfLocalDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function getMoodConfig(mood) {
  if (!mood) return { label: 'Unknown', emoji: '•', category: 'neutral' };
  const key = mood.toLowerCase();
  return MOOD_CONFIG[key] || { label: mood, emoji: '•', category: 'neutral' };
}

function ratingToY(rating) {
  const clamped = Math.max(-2, Math.min(2, typeof rating === 'number' ? rating : 0));
  const t = (clamped + 2) / 4;
  return PAD_TOP + (1 - t) * USABLE_H;
}

function mean(nums) {
  if (!nums.length) return 0;
  return nums.reduce((s, n) => s + n, 0) / nums.length;
}

function mondayOf(d) {
  const x = startOfLocalDay(d);
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  return x;
}

function thursdayOfWeek(monday) {
  const t = new Date(monday);
  t.setDate(t.getDate() + 3);
  return t;
}

/** Distinct mood categories per day → mean valence (count does not inflate). */
function buildDailyBuckets(rawMoodData) {
  const byDate = new Map();
  rawMoodData.forEach((item) => {
    if (!item?.date) return;
    if (!byDate.has(item.date)) byDate.set(item.date, new Map());
    const moodKey = (item.mood || '').toLowerCase();
    const rating = typeof item.category === 'number' ? item.category : 0;
    byDate.get(item.date).set(moodKey || `_${rating}`, {
      mood: item.mood,
      rating,
      updated_at: item.updated_at || null,
      config: getMoodConfig(item.mood),
    });
  });

  return Array.from(byDate.entries())
    .map(([date, moodMap]) => {
      const logs = Array.from(moodMap.values()).sort(
        (a, b) => new Date(a.updated_at || 0) - new Date(b.updated_at || 0)
      );
      const ratings = logs.map((l) => l.rating);
      const avg = mean(ratings);
      const min = Math.min(...ratings);
      const max = Math.max(...ratings);
      return {
        id: date,
        date,
        kind: 'day',
        mean: avg,
        min,
        max,
        distinctCount: logs.length,
        spread: max - min,
        logs,
        xDate: parseLocalDate(date),
      };
    })
    .sort((a, b) => a.xDate - b.xDate);
}

function buildWeeklyBuckets(dailyBuckets, domainStart, domainEnd) {
  const weeks = new Map();
  dailyBuckets.forEach((day) => {
    if (day.xDate < domainStart || day.xDate > domainEnd) return;
    const mon = mondayOf(day.xDate);
    const key = formatDateKey(mon);
    if (!weeks.has(key)) weeks.set(key, { monday: mon, days: [] });
    weeks.get(key).days.push(day);
  });

  return Array.from(weeks.values())
    .map(({ monday, days }) => {
      const means = days.map((d) => d.mean);
      const avg = mean(means);
      const min = Math.min(...means);
      const max = Math.max(...means);
      const mid = thursdayOfWeek(monday);
      const weekEnd = new Date(monday);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return {
        id: formatDateKey(monday),
        date: formatDateKey(monday),
        kind: 'week',
        mean: avg,
        min,
        max,
        distinctCount: days.length,
        spread: max - min,
        logs: days.flatMap((d) => d.logs),
        days,
        monday,
        weekEnd,
        xDate: mid < domainStart ? domainStart : mid > domainEnd ? domainEnd : mid,
      };
    })
    .sort((a, b) => a.xDate - b.xDate);
}

function getDomain(days) {
  const end = startOfLocalDay(new Date());
  const start = new Date(end);
  start.setDate(start.getDate() - (days - 1));
  return { start, end };
}

function dateToX(date, domainStart, domainEnd, width) {
  const innerW = Math.max(1, width - PAD_X * 2);
  const span = Math.max(1, domainEnd - domainStart);
  const t = (startOfLocalDay(date) - domainStart) / span;
  return PAD_X + Math.max(0, Math.min(1, t)) * innerW;
}

function catmullRomPath(points) {
  if (points.length < 2) return '';
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function segmentBuckets(buckets, kind) {
  if (!buckets.length) return [];
  const gapMs = kind === 'week' ? 8 * MS_DAY : 1.5 * MS_DAY;
  const segments = [[buckets[0]]];
  for (let i = 1; i < buckets.length; i++) {
    const prev = buckets[i - 1];
    const cur = buckets[i];
    if (cur.xDate - prev.xDate > gapMs) segments.push([cur]);
    else segments[segments.length - 1].push(cur);
  }
  return segments;
}

function buildXLabels(rangeId, domainStart, domainEnd, width) {
  const labels = [];
  const push = (d, edge) => {
    labels.push({ date: d, x: dateToX(d, domainStart, domainEnd, width), edge });
  };

  if (rangeId === '2W') {
    for (let d = new Date(domainStart); d <= domainEnd; d.setDate(d.getDate() + 2)) {
      push(new Date(d), null);
    }
    if (labels[labels.length - 1]?.date.getTime() !== domainEnd.getTime()) {
      push(new Date(domainEnd), 'end');
    }
  } else if (rangeId === '1M') {
    for (let k = 0; k < 5; k++) {
      const t = k / 4;
      const d = new Date(domainStart.getTime() + t * (domainEnd - domainStart));
      push(startOfLocalDay(d), k === 0 ? 'start' : k === 4 ? 'end' : null);
    }
  } else if (rangeId === '3M') {
    const cur = new Date(domainStart.getFullYear(), domainStart.getMonth(), 1);
    if (cur < domainStart) cur.setMonth(cur.getMonth() + 1);
    while (cur <= domainEnd) {
      push(new Date(cur), null);
      cur.setMonth(cur.getMonth() + 1);
    }
  } else {
    const cur = new Date(domainStart.getFullYear(), domainStart.getMonth(), 1);
    if (cur < domainStart) cur.setMonth(cur.getMonth() + 1);
    let i = 0;
    while (cur <= domainEnd) {
      if (i % 2 === 0) push(new Date(cur), null);
      cur.setMonth(cur.getMonth() + 1);
      i += 1;
    }
  }

  if (labels.length) {
    labels[0].edge = 'start';
    labels[labels.length - 1].edge = 'end';
  }
  return labels;
}

function formatShort(d) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatMonth(d) {
  return d.toLocaleDateString('en-US', { month: 'short' });
}

function formatValence(v) {
  const r = Math.round(v * 10) / 10;
  return r > 0 ? `+${r}` : `${r}`;
}

function computeInsight(buckets, allDaily, rangeDays, rangeId) {
  if (buckets.length < 3) {
    return { text: 'Log a few days to see your pattern', delta: null };
  }
  const avg = mean(buckets.map((b) => b.mean));
  const { start } = getDomain(rangeDays);
  const priorEnd = new Date(start);
  priorEnd.setDate(priorEnd.getDate() - 1);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - (rangeDays - 1));
  const prior = allDaily.filter((d) => d.xDate >= priorStart && d.xDate <= priorEnd);
  const periodLabel = rangeId === '2W' ? 'last 2 weeks' : rangeId === '1M' ? 'last month' : rangeId === '3M' ? 'prior 3 months' : 'last year';

  if (prior.length < 3) {
    return { text: `Avg ${formatValence(avg)}`, delta: null };
  }
  const priorAvg = mean(prior.map((d) => d.mean));
  const delta = avg - priorAvg;
  let cmp = 'steadier';
  if (delta >= 0.3) cmp = 'brighter';
  else if (delta <= -0.3) cmp = 'heavier';
  return {
    text: `Avg ${formatValence(avg)}`,
    delta: `${cmp} than ${periodLabel}`,
  };
}

function getSampleDaily() {
  const out = [];
  const pattern = [0.4, 1.2, 0.8, -0.4, 0.2, 1.6, 0.6, -1.0, -0.2, 0.8, 1.4, 0.2, -0.6, 1.0];
  for (let i = pattern.length - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const v = pattern[pattern.length - 1 - i];
    const mood = v >= 1 ? 'joy' : v <= -1 ? 'sad' : 'content';
    out.push({
      id: formatDateKey(d),
      date: formatDateKey(d),
      kind: 'day',
      mean: v,
      min: v,
      max: v,
      distinctCount: 1,
      spread: 0,
      logs: [{ mood, rating: v, config: getMoodConfig(mood) }],
      xDate: startOfLocalDay(d),
    });
  }
  return out;
}

function MoodChart({ expandTrackMood = false, onExpandTrackMoodHandled }) {
  const [moodData, setMoodData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rangeId, setRangeId] = useState('1M');
  const [selected, setSelected] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [moodCategories, setMoodCategories] = useState(null);
  const [postingMood, setPostingMood] = useState(false);
  const [postError, setPostError] = useState(null);
  const [trackSuccess, setTrackSuccess] = useState(false);
  const [chartW, setChartW] = useState(320);
  const plotRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    loadMoodData();
  }, []);

  useEffect(() => {
    if (!expandTrackMood || !onExpandTrackMoodHandled) return;
    setSheetOpen(true);
    const t = setTimeout(() => onExpandTrackMoodHandled(), 50);
    return () => clearTimeout(t);
  }, [expandTrackMood, onExpandTrackMoodHandled]);

  useEffect(() => {
    const el = plotRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 40) setChartW(Math.floor(w));
    });
    ro.observe(el);
    setChartW(Math.floor(el.clientWidth) || 320);
    return () => ro.disconnect();
  }, [loading]);

  const loadMoodData = useCallback(async () => {
    try {
      setLoading(true);
      const user = await getCurrentUser();
      const uid = user?.userId;
      const cached = uid ? getChartCache(CHART_KEYS.moods, uid) : null;
      if (cached != null && Array.isArray(cached) && cached.length > 0) {
        setMoodData(cached);
        setLoading(false);
      }
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const response = await fetch(MOODS_ENDPOINT, {
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to load mood data');
      const data = await response.json();
      const payload = data.statusCode != null ? JSON.parse(data.body || '{}') : data;
      const moods = payload.moods || [];
      setMoodData(moods);
      setError(null);
      if (uid) setChartCache(CHART_KEYS.moods, uid, moods);
    } catch (err) {
      console.error('Error loading moods:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sheetOpen || moodCategories) return;
    let cancelled = false;
    (async () => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        const res = await fetch(MOOD_CATEGORIES_ENDPOINT, {
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to load mood categories');
        const data = await res.json();
        const payload = data.statusCode != null ? JSON.parse(data.body || '{}') : data;
        if (!cancelled) setMoodCategories(payload);
      } catch (err) {
        if (!cancelled) setPostError(err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sheetOpen, moodCategories]);

  const submitMood = async (mood, valence = 0) => {
    setPostError(null);
    setPostingMood(true);
    try {
      const today = formatDateKey(new Date());
      setMoodData((prev) => [
        ...prev,
        {
          date: today,
          mood,
          category: typeof valence === 'number' ? valence : 0,
          count: 1,
          updated_at: new Date().toISOString(),
        },
      ]);

      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      const res = await fetch(MOODS_ENDPOINT, {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save mood');
      }
      setTrackSuccess(true);
      await loadMoodData();
      setTimeout(() => {
        setTrackSuccess(false);
        setSheetOpen(false);
      }, 1200);
    } catch (err) {
      setPostError(err.message || 'Failed to save mood');
      await loadMoodData();
    } finally {
      setPostingMood(false);
    }
  };

  const range = RANGE_OPTIONS.find((r) => r.id === rangeId) || RANGE_OPTIONS[1];
  const hasRealData = moodData.length > 0;
  const allDaily = useMemo(
    () => (hasRealData ? buildDailyBuckets(moodData) : getSampleDaily()),
    [moodData, hasRealData]
  );

  const { start: domainStart, end: domainEnd } = useMemo(() => getDomain(range.days), [range.days]);

  const buckets = useMemo(() => {
    const inRange = allDaily.filter((d) => d.xDate >= domainStart && d.xDate <= domainEnd);
    if (range.bucket === 'week') return buildWeeklyBuckets(allDaily, domainStart, domainEnd);
    return inRange;
  }, [allDaily, domainStart, domainEnd, range.bucket]);

  const insight = useMemo(
    () => computeInsight(buckets, allDaily, range.days, rangeId),
    [buckets, allDaily, range.days, rangeId]
  );

  const xLabels = useMemo(
    () => buildXLabels(rangeId, domainStart, domainEnd, chartW),
    [rangeId, domainStart, domainEnd, chartW]
  );

  const plotted = useMemo(
    () =>
      buckets.map((b) => ({
        ...b,
        x: dateToX(b.xDate, domainStart, domainEnd, chartW),
        y: ratingToY(b.mean),
      })),
    [buckets, domainStart, domainEnd, chartW]
  );

  const segments = useMemo(() => segmentBuckets(plotted, range.bucket), [plotted, range.bucket]);

  useEffect(() => {
    setSelected(null);
  }, [rangeId]);

  useEffect(() => {
    if (!selected) return;
    const el = document.querySelector('.mood-day-detail');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  const selectNearest = useCallback(
    (clientX) => {
      if (!plotted.length || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * chartW;
      let best = plotted[0];
      let bestDist = Math.abs(plotted[0].x - x);
      for (let i = 1; i < plotted.length; i++) {
        const dist = Math.abs(plotted[i].x - x);
        if (dist < bestDist) {
          best = plotted[i];
          bestDist = dist;
        }
      }
      setSelected(best);
    },
    [plotted, chartW]
  );

  const onKeyDown = (e) => {
    if (!plotted.length) return;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      const idx = selected ? plotted.findIndex((p) => p.id === selected.id) : -1;
      const next =
        e.key === 'ArrowLeft'
          ? plotted[Math.max(0, (idx < 0 ? plotted.length : idx) - 1)]
          : plotted[Math.min(plotted.length - 1, idx + 1)];
      setSelected(next);
    } else if (e.key === 'Enter' && selected) {
      const el = document.querySelector('.mood-day-detail');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const yTicks = [
    { v: 2, label: '+2' },
    { v: 0, label: '0' },
    { v: -2, label: '−2' },
  ];

  let mainContent;
  if (loading) {
    mainContent = <div className="mood-chart-loading">Loading mood data…</div>;
  } else if (error) {
    mainContent = <div className="mood-chart-error">Unable to load mood data</div>;
  } else {
    mainContent = (
      <>
        {!hasRealData && (
          <div className="chart-cta-new-user" role="status">
            <p className="chart-cta-new-user-headline">Start tracking to see your mood pattern.</p>
            <p className="chart-cta-new-user-note">Sample timeline shown.</p>
          </div>
        )}

        <div className="mood-insight-row">
          <p className="mood-insight-text">
            {insight.text}
            {insight.delta ? <span className="mood-insight-delta"> · {insight.delta}</span> : null}
          </p>
          <div className="mood-ramp-legend" aria-hidden>
            <span>low</span>
            <span className="mood-ramp-pill" />
            <span>great</span>
          </div>
        </div>

        <div className="mood-plot-layout">
          <div className="mood-yaxis" aria-hidden>
            {yTicks.map((t) => (
              <span
                key={t.v}
                className="mood-ytick"
                style={{ top: `${(ratingToY(t.v) / CHART_H) * 100}%` }}
              >
                {t.label}
              </span>
            ))}
          </div>

          <div className="mood-plot-wrap" ref={plotRef}>
            <svg
              ref={svgRef}
              className="mood-plot-svg"
              width={chartW}
              height={CHART_H}
              viewBox={`0 0 ${chartW} ${CHART_H}`}
              role="img"
              aria-label="Mood valence timeline"
              tabIndex={0}
              onKeyDown={onKeyDown}
              onPointerDown={(e) => selectNearest(e.clientX)}
            >
              <defs>
                <linearGradient id="mood-zone-pos" x1="0" y1="1" x2="0" y2="0">
                  <stop offset="0%" stopColor="#3d7ea6" stopOpacity="0.06" />
                  <stop offset="100%" stopColor="#3d7ea6" stopOpacity="0.02" />
                </linearGradient>
                <linearGradient id="mood-zone-neg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#5a8fb0" stopOpacity="0.02" />
                  <stop offset="100%" stopColor="#5a8fb0" stopOpacity="0.07" />
                </linearGradient>
                <linearGradient
                  id="mood-line-grad"
                  gradientUnits="userSpaceOnUse"
                  x1="0"
                  y1={PAD_TOP}
                  x2="0"
                  y2={PAD_TOP + USABLE_H}
                >
                  <stop offset="0%" stopColor="#3D7EA6" stopOpacity="0.5" />
                  <stop offset="50%" stopColor="#A8C8DC" stopOpacity="0.5" />
                  <stop offset="100%" stopColor="#5A8FB0" stopOpacity="0.5" />
                </linearGradient>
              </defs>

              <rect x="0" y={PAD_TOP} width={chartW} height={USABLE_H / 2} fill="url(#mood-zone-pos)" />
              <rect
                x="0"
                y={PAD_TOP + USABLE_H / 2}
                width={chartW}
                height={USABLE_H / 2}
                fill="url(#mood-zone-neg)"
              />
              <line
                x1="0"
                y1={ratingToY(0)}
                x2={chartW}
                y2={ratingToY(0)}
                stroke="#C5D4E0"
                strokeWidth="1"
                strokeDasharray="3 4"
              />

              {!plotted.length && (
                <text
                  x={chartW / 2}
                  y={PAD_TOP + USABLE_H / 2}
                  textAnchor="middle"
                  fill="#5A6B7D"
                  fontSize="13"
                >
                  No moods in this range
                </text>
              )}

              {selected && (
                <line
                  x1={selected.x}
                  y1={PAD_TOP}
                  x2={selected.x}
                  y2={PAD_TOP + USABLE_H}
                  stroke="rgba(26,35,50,0.12)"
                  strokeWidth="1"
                />
              )}

              {segments.map((seg, si) => {
                const pts = seg.map((p) => ({ x: p.x, y: p.y }));
                const d = catmullRomPath(pts);
                return d ? (
                  <path
                    key={`seg-${si}`}
                    d={d}
                    fill="none"
                    stroke="url(#mood-line-grad)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : null;
              })}

              {plotted.map((p) => {
                const isSel = selected?.id === p.id;
                const fill = valenceColor(p.mean);
                const showWhisker =
                  range.whiskers && p.distinctCount >= 2 && p.spread >= 3;
                const r = isSel ? 5.5 : range.dotR;
                return (
                  <g key={p.id}>
                    {showWhisker && (
                      <line
                        x1={p.x}
                        y1={ratingToY(p.max)}
                        x2={p.x}
                        y2={ratingToY(p.min)}
                        stroke={fill}
                        strokeOpacity="0.4"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    )}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={r}
                      fill={fill}
                      stroke={isSel ? '#1A2332' : '#FFFFFF'}
                      strokeWidth={isSel ? 2 : 1.5}
                    />
                  </g>
                );
              })}
            </svg>

            <div className="mood-xaxis" style={{ width: chartW }}>
              {xLabels.map((lab, i) => (
                <span
                  key={`${lab.date.getTime()}-${i}`}
                  className={`mood-xlabel${lab.edge === 'start' ? ' is-start' : ''}${
                    lab.edge === 'end' ? ' is-end' : ''
                  }`}
                  style={{ left: `${(lab.x / chartW) * 100}%` }}
                >
                  {rangeId === '3M' || rangeId === '1Y'
                    ? formatMonth(lab.date)
                    : formatShort(lab.date)}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mood-range-wrap">
          <div className="mood-range-seg" role="tablist" aria-label="Mood range">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={rangeId === opt.id}
                className={`mood-range-seg-btn${rangeId === opt.id ? ' is-active' : ''}`}
                onClick={() => setRangeId(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mood-log-wrap">
          <button type="button" className="mood-log-btn" onClick={() => setSheetOpen(true)}>
            ＋ Log mood
          </button>
        </div>

        {selected && (
          <div className="mood-day-detail" role="region" aria-label="Selected mood detail">
            <div className="mood-day-detail-header">
              <div>
                <h3 className="mood-day-detail-title">
                  {selected.kind === 'week'
                    ? `${formatShort(selected.monday)} – ${selected.weekEnd.getDate()}`
                    : parseLocalDate(selected.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                      })}
                </h3>
                <p className="mood-day-detail-sub">
                  Avg {formatValence(selected.mean)}
                  {selected.kind === 'week'
                    ? ` · ${selected.days?.length || 0} days logged`
                    : ` · ${selected.logs.length} log${selected.logs.length === 1 ? '' : 's'}`}
                </p>
              </div>
              <button
                type="button"
                className="mood-day-detail-close"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <ul className="mood-day-logs">
              {selected.kind === 'week'
                ? (selected.days || []).map((day) => {
                    const top = day.logs[0];
                    return (
                      <li key={day.id} className="mood-day-log">
                        <span className="mood-day-log-emoji">{top?.config?.emoji || '•'}</span>
                        <span className="mood-day-log-label">
                          {formatShort(day.xDate)}
                          {day.logs.length > 1 ? ` · ${day.logs.length} moods` : ` · ${top?.config?.label || top?.mood}`}
                        </span>
                        <span className="mood-day-log-rating" style={{ color: valenceColor(day.mean) }}>
                          {formatValence(day.mean)}
                        </span>
                      </li>
                    );
                  })
                : selected.logs.map((log, idx) => (
                    <li key={`${log.mood}-${idx}`} className="mood-day-log">
                      <span className="mood-day-log-emoji">{log.config?.emoji || '•'}</span>
                      <span className="mood-day-log-label">{log.config?.label || log.mood}</span>
                      <span className="mood-day-log-rating" style={{ color: valenceColor(log.rating) }}>
                        {log.rating > 0 ? `+${log.rating}` : log.rating}
                      </span>
                    </li>
                  ))}
            </ul>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="mood-chart-container">
      {mainContent}
      <AddMoodSheet
        open={sheetOpen}
        onClose={() => {
          if (!postingMood) {
            setSheetOpen(false);
            setPostError(null);
            setTrackSuccess(false);
          }
        }}
        moodCategories={moodCategories}
        onSubmit={submitMood}
        posting={postingMood}
        error={postError}
        success={trackSuccess}
      />
    </div>
  );
}

export default MoodChart;
